import React, { FC, useState, memo, useCallback } from 'react';
import './SortListBundle.scss';
import { setMultipleOrderDeviation } from 'utils/deviationsUpdate';
import SortListOrder, { ISortListOrder } from './SortListOrder';
import clsx from 'clsx';
import { useDeepCompareEffect } from 'use-deep-compare';
import { MdExpandLess } from 'react-icons/all';
import { MdExpandMore } from 'react-icons/md';
import Collapse from '@material-ui/core/Collapse';

interface ColorMapping {
  [key: string]: string;
}

const shelfIdColorMapping: ColorMapping = {
  FINISHED: 'green',
  STARTED: 'yellow',
  ORDERS_REMAINING: 'red',
  PLACEHOLDER: 'black',
  UNEXPECTED: 'purple',
};

enum SortListBundleStatus {
  FINISHED,
  STARTED,
  ORDERS_REMAINING,
  PLACEHOLDER,
  UNEXPECTED,
}

export type ISortListBundle = {
  status: SortListBundleStatus;
  id: number;
  shelfId: string;
  remaining: number;
  inProgress: number;
  scanned: number;
  driver?: string;
  orders: ISortListOrder[];
};

type RemoveOrdersButtonProps = {
  orderCount: number;
  onClick: () => void;
};

const RemoveOrdersButton = ({
  orderCount,
  onClick,
}: RemoveOrdersButtonProps) => (
  <div className="order-scanner-table-remove-button" onClick={onClick}>
    <p>
      Remove {orderCount} order(s)? <span>CONFIRM</span>
    </p>
  </div>
);

const SortListBundle: FC<{
  bundleData: ISortListBundle;
  updateHubSortListEntries: (bundleIds: string[]) => void;
}> = memo(({ bundleData, updateHubSortListEntries }) => {
  const [ordersToRemove, setOrdersToRemove] = useState<Set<number>>(new Set());
  const [numParcels, setNumParcels] = useState<number>(0);
  const [isOrdersVisible, toggleOrdersVisibility] = useState<boolean>(false);

  useDeepCompareEffect(() => {
    const parcelCount = bundleData.orders.reduce(
      (acc, order) => acc + order.parcels,
      0
    );
    setNumParcels(parcelCount);
  }, [bundleData.orders]);

  // Distributed to each order in bundle, so orders to remove
  // will be registered here and requested to backend later.
  const toggleRemoval = useCallback(
    (id: number) => {
      const ordersToRemoveCopy = new Set(ordersToRemove);
      ordersToRemove.has(id)
        ? ordersToRemoveCopy.delete(id)
        : ordersToRemoveCopy.add(id);
      setOrdersToRemove(ordersToRemoveCopy);
    },
    [ordersToRemove]
  );

  const removeOrders = useCallback(async () => {
    await setMultipleOrderDeviation([...ordersToRemove].map(e => e.toString()));
    setOrdersToRemove(new Set());
    updateHubSortListEntries([bundleData.id.toString()]);
  }, [bundleData.id, ordersToRemove, updateHubSortListEntries]);

  return (
    <div className="sortListEntry">
      <div className="sortListEntry topBar">
        <div
          className={clsx(
            'sortListEntry',
            'topBar',
            'corner',
            'centerChild',
            `sortListEntry__bgcolor-${shelfIdColorMapping[bundleData.status]}`
          )}
        >
          {bundleData.shelfId}
        </div>
        <div className="sortListEntry topBar middle">
          <p>Remaining</p>
          <div className="sortListEntry topBar middle red">
            {bundleData.remaining}
          </div>
        </div>
        <div className="sortListEntry topBar middle">
          <p>In progress</p>
          <div className="sortListEntry topBar middle yellow">
            {bundleData.inProgress}
          </div>
        </div>
        <div className="sortListEntry topBar middle">
          <p>Scanned</p>
          <div className="sortListEntry topBar middle green">
            {bundleData.scanned}
          </div>
        </div>
        <div className="sortListEntry topBar corner centerChild sortListEntry__bgcolor-red">
          {bundleData.id}
        </div>
      </div>
      <div className="sortListEntry__driver">DRIVER: ...</div>
      {isOrdersVisible ? (
        <div
          className="sortListEntry__collapse-section"
          onClick={() => toggleOrdersVisibility(currentState => !currentState)}
        >
          <MdExpandLess />
          <span>Collapse orders</span>
        </div>
      ) : null}
      <Collapse in={isOrdersVisible} timeout="auto" collapsedHeight={190}>
        <>
          <div className="sortListEntry__orders">
            <div />
            <p>ORDER ID</p>
            <p>PARCELS</p>
            <p>RETAILER</p>
            <p>HUB</p>
            <p>SENDER</p>
            <p>UPDATE PARCELS</p>
            <p>REMOVE</p>
            {(isOrdersVisible
              ? bundleData.orders
              : bundleData.orders.slice(0, 4)
            ).map((orderData, i) => (
              <SortListOrder
                orderData={orderData}
                removalChecked={ordersToRemove.has(orderData.id)}
                toggleRemoval={toggleRemoval}
                index={i}
                bundleId={bundleData.id}
                updateHubSortListEntries={updateHubSortListEntries}
                key={orderData.id}
              />
            ))}
          </div>
          {ordersToRemove.size > 0 ? (
            <RemoveOrdersButton
              orderCount={ordersToRemove.size}
              onClick={removeOrders}
            />
          ) : null}
          <div className="sortListEntry__summary">
            <p>ORDERS: {bundleData.orders.length}</p>
            <p>PARCELS: {numParcels}</p>
          </div>
        </>
      </Collapse>
      <div
        className="sortListEntry__collapse-section"
        onClick={() => toggleOrdersVisibility(currentState => !currentState)}
      >
        {isOrdersVisible ? <MdExpandLess /> : <MdExpandMore />}
        <span>{isOrdersVisible ? 'Collapse orders' : 'See all orders'}</span>
      </div>
    </div>
  );
});

SortListBundle.displayName = 'SortListBundle';
SortListBundle.whyDidYouRender = true;
export default SortListBundle;
